import { createContext, FC, ReactNode, useContext } from 'react';

export type AnalyticsExportThemeName = 'light' | 'dark';

interface AnalyticsExportThemeTokens {
  page: string;
  title: string;
  subtitle: string;
  tile: string;
  /** Charts paint their axis labels dark, so this surface stays light. */
  chartSurface: string;
  tileLabel: string;
  tileValue: string;
  accent: string;
  footer: string;
  neutralBadge: string;
  mediaPlaceholder: string;
}

/**
 * Light is what the PDF prints: the sheet is white so the margins the
 * screenshot service adds stay invisible. Dark is the on-screen presentation,
 * where nothing is printed and the gradient can run edge to edge.
 */
const ANALYTICS_EXPORT_THEMES: Record<
  AnalyticsExportThemeName,
  AnalyticsExportThemeTokens
> = {
  light: {
    page: 'tw-bg-white',
    title: 'tw-text-grayscale-190',
    subtitle: 'tw-text-grayscale-120',
    tile: 'tw-bg-[#F3EEFE]',
    chartSurface: 'tw-bg-[#F3EEFE]',
    tileLabel: 'tw-text-grayscale-180',
    tileValue: 'tw-text-grayscale-190',
    accent: 'tw-text-[#6D4AE0]',
    footer: 'tw-text-grayscale-100',
    neutralBadge: 'tw-bg-grayscale-20 tw-text-grayscale-190',
    mediaPlaceholder: 'tw-bg-grayscale-10',
  },
  dark: {
    page: 'tw-bg-gradient-ai-1',
    title: 'tw-text-white',
    subtitle: 'tw-text-white/70',
    tile: 'tw-bg-white/20',
    chartSurface: 'tw-bg-white/90',
    tileLabel: 'tw-text-white',
    tileValue: 'tw-text-white',
    accent: 'tw-text-white/70',
    footer: 'tw-text-white/70',
    neutralBadge: 'tw-bg-white/30 tw-text-white',
    mediaPlaceholder: 'tw-bg-white/10',
  },
};

const AnalyticsExportThemeContext = createContext<AnalyticsExportThemeTokens>(
  ANALYTICS_EXPORT_THEMES.light,
);

export const AnalyticsExportThemeProvider: FC<{
  theme: AnalyticsExportThemeName;
  children: ReactNode;
}> = ({ theme, children }) => (
  <AnalyticsExportThemeContext.Provider value={ANALYTICS_EXPORT_THEMES[theme]}>
    {children}
  </AnalyticsExportThemeContext.Provider>
);

export function useAnalyticsExportTheme(): AnalyticsExportThemeTokens {
  return useContext(AnalyticsExportThemeContext);
}
